<i18n src="@/i18n/enums/support_urgency.json"></i18n>
<template>
	<!-- Leitura: termômetro compacto + nome do nível !-->
	<div v-if="readonly" class="inline-flex items-center gap-2">
		<span class="flex items-center gap-0.5" aria-hidden="true">
			<span v-for="(level, index) in levels"
				  :key="level.id"
				  class="block h-1.5 w-4 rounded-sm"
				  :class="isFilled(index) ? markClass : 'bg-gray-200'"></span>
		</span>
		<span class="text-xs font-semibold" :class="textClass">{{ currentLabel }}</span>
	</div>
	<!-- Edição: as paradas nomeadas sobre um termômetro que enche até a escolhida !-->
	<div v-else class="max-w-sm">
		<div role="radiogroup"
			 :aria-label="label"
			 @keydown.left.prevent="handleStep(-1)"
			 @keydown.up.prevent="handleStep(-1)"
			 @keydown.right.prevent="handleStep(1)"
			 @keydown.down.prevent="handleStep(1)">
			<!-- Paradas !-->
			<div class="grid grid-cols-4" @mouseleave="hoverIndex = -1">
				<button v-for="(level, index) in levels"
						:key="level.id"
						type="button"
						role="radio"
						:aria-checked="isCurrent(index) ? 'true' : 'false'"
						:tabindex="isCurrent(index) || (currentIndex < 0 && index === 0) ? 0 : -1"
						class="rounded px-1 pt-1 pb-2 text-xs sm:text-sm transition-colors motion-reduce:transition-none focus:outline-none focus-visible:bg-gray-100 focus-visible:text-gray-900"
						:class="labelClass(index)"
						@mouseenter="hoverIndex = index"
						@focus="hoverIndex = index"
						@blur="hoverIndex = -1"
						@click="handleSelect(level)">
					{{ level.label }}
				</button>
			</div>
			<!-- Termômetro: a prévia mostra até onde a barra vai antes do clique !-->
			<div class="relative h-1.5 overflow-hidden rounded-full bg-gray-200">
				<div class="absolute inset-y-0 left-0 rounded-full bg-gray-300 transition-all duration-150 motion-reduce:transition-none"
					 :style="{width: previewWidth}"></div>
				<div class="absolute inset-y-0 left-0 rounded-full transition-all duration-200 motion-reduce:transition-none"
					 :class="markClass"
					 :style="{width: fillWidth}"></div>
			</div>
		</div>
		<!-- Error !-->
		<ErrorBag v-if="validation" :validation="validation"/>
	</div>
</template>

<script>
import ErrorBag from "@/components/ErrorBag.vue"

import {options as SupportUrgencyOptions} from "@/enums/support_urgency.js"

const MARK_COLORS = {
	10: "bg-gray-400",
	20: "bg-blue-500",
	30: "bg-orange-500",
	40: "bg-red-500"
}

const TEXT_COLORS = {
	10: "text-gray-600",
	20: "text-blue-700",
	30: "text-orange-700",
	40: "text-red-700"
}

export default {
	name: "SupportUrgencyMeter",
	components: {
		ErrorBag
	},
	props: {
		value: [String, Number],
		validation: Object,
		readonly: Boolean,
		label: {
			type: String,
			default() {
				return this.$t('support_urgency.label')
			}
		}
	},
	data() {
		return {
			hoverIndex: -1
		}
	},
	computed: {
		levels() {
			return translateEnum(this, SupportUrgencyOptions, 'support_urgency.')
		},
		/**
		 * Posição do nível escolhido na escala
		 * @returns {Number}
		 */
		currentIndex() {
			return this.levels.findIndex(level => level.id == this.value)
		},
		currentLabel() {
			return this.levels[this.currentIndex]?.label ?? '—'
		},
		markClass() {
			return MARK_COLORS[this.value] ?? "bg-gray-400"
		},
		/**
		 * O termômetro enche até o fim da parada escolhida
		 * @returns {String}
		 */
		fillWidth() {
			return this.widthUpTo(this.currentIndex)
		},
		/**
		 * Prévia: até onde a barra chega se o operador clicar na parada sob o cursor
		 * @returns {String}
		 */
		previewWidth() {
			return this.widthUpTo(this.hoverIndex)
		},
		textClass() {
			return TEXT_COLORS[this.value] ?? "text-gray-500"
		}
	},
	methods: {
		/**
		 * Parada escolhida
		 * @param index
		 * @returns {boolean}
		 */
		isCurrent(index) {
			return this.currentIndex === index
		},
		/**
		 * Parada acesa: está antes ou é a escolhida
		 * @param index
		 * @returns {boolean}
		 */
		isFilled(index) {
			return this.currentIndex >= index
		},
		/**
		 * Largura do trilho até o fim de uma parada
		 * @param index
		 * @returns {String}
		 */
		widthUpTo(index) {
			if (index < 0) return '0%'

			return `${(index + 1) / this.levels.length * 100}%`
		},
		/**
		 * @param index
		 * @returns {Array}
		 */
		labelClass(index) {
			const hovered = this.hoverIndex === index

			if (this.isCurrent(index)) {
				return ["font-semibold", this.textClass, hovered ? "bg-gray-100" : ""]
			}

			return hovered ? ["bg-gray-100", "text-gray-900"] : ["text-gray-500"]
		},
		/**
		 * Anda um nível na escala pelo teclado
		 * @param direction
		 */
		handleStep(direction) {
			const index = Math.min(Math.max(this.currentIndex + direction, 0), this.levels.length - 1)

			this.handleSelect(this.levels[index])

			this.$nextTick(() => this.$el.querySelectorAll('button')[index]?.focus())
		},
		/**
		 * @param level
		 */
		handleSelect(level) {
			if (!level) return

			this.$emit('input', level.id)
			this.$emit('change', level.id)
		}
	}
}
</script>
